import React, { useState, useCallback, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@mui/styles';
import { Grid, Card, CardContent, Typography, useMediaQuery } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import DynamoProductSearch from '../../Queries/DynamoProductSearch.js';
import SearchResultsSkeleton from '../../Skeletons/SearchResultsSkeleton.js';
import ListView from '../../Store/ListView.js';
import StoreButton from '../../Forms/Store/StoreButton.js';
import BBDrawer from '../../Drawer/BBDrawer.js';
import LoadMoreScrollAnchor from '../../LoadMoreScrollAnchor.js';
import { convertLangCodeToGraphqlEnum } from '../../../helpers/locale.js';
import FilterPicker from './FilterPicker.js';
import FilterButton from './FilterButton.js';
import OrderBy from './OrderBy.js';
import BBTranslate from '../../Locale/BBTranslate.js';
import { LocaleContext } from '../../../Context';
import {
  getSelectedTagsFromSearchParams,
  getSearchParamsFromSelectedTags
} from '../../../helpers/productTagFilter.js';

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: '3rem',
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      flexDirection: 'column-reverse'
    }
  },
  sortAndFilter: {
    position: 'absolute',
    top: 0,
    right: 0,
    [theme.breakpoints.down('md')]: {
      position: 'relative',
      display: 'flex',
      marginBottom: '2rem',
      '& .MuiFormControl-root': {
        width: 'auto'
      }
    }
  },
  filterByButton: {
    marginLeft: '1rem',
    [theme.breakpoints.down('sm')]: {
      flexGrow: 1
    }
  },
  filterContainer: {
    boxShadow: theme.boxShadow,
    marginRight: '2rem'
  },
  updateButton: {
    position: 'sticky',
    width: '100%',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '1rem 1rem 2rem 1rem',
    '& .MuiButton-root': {
      width: '100%'
    }
  },
  loadMore: {
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      justifyContent: 'center'
    },
    [theme.breakpoints.down('sm')]: {
      '& button': {
        width: '100%'
      }
    }
  },
  noProducts: {
    textAlign: 'center',
    marginTop: '3rem'
  }
}));

const ProductsWithFilterPicker = (props) => {
  const {
    headerText,
    hardcodedTags,
    availableTagsByGroupName,
    enabledTagsByGroupName,
    onContentLanguageChange,
    contentLanguage,
    showContentLanguagePicker,
    hideAuthor
  } = props;
  const { locale } = useContext(LocaleContext);

  const [searchParams, setSearchParams] = useSearchParams();
  const classes = useStyles();
  const [orderBy, setOrderBy] = useState('id');

  const initialSelectedTags = getSelectedTagsFromSearchParams(searchParams);
  const [selectedTagsByGroup, setSelectedTagsByGroup] = useState(initialSelectedTags);

  const theme = useTheme();
  const filtersInDrawer = useMediaQuery(theme.breakpoints.down('md'));
  const [filtersOpen, setFiltersOpen] = useState(false);

  const handleSelectedTagsChange = (groupName, clickedTag, checked) => {
    setSelectedTagsByGroup((prevTags) => {
      const nextTags = { ...prevTags };
      let nextGroupTags = [...nextTags[groupName]];

      if (checked) {
        nextGroupTags.push(clickedTag);
      } else {
        const idx = nextGroupTags.findIndex(tag => tag === clickedTag);
        nextGroupTags.splice(idx, 1);
      }

      // we no longer have 'Any system' as a filter option but instead
      // we want to get any products marked as 'Any sytem' to be returned
      // in any system tag filter - #251
      if (groupName === 'System' && nextGroupTags.length === 1) {
        if (checked) {
          // add 'Any System'
          nextGroupTags.push('Any system');
        } else {
          // remove 'Any System'
          nextGroupTags = [];
        }
      }

      nextTags[groupName] = nextGroupTags;

      return nextTags;
    });
  };

  const handleOrderByChange = useCallback((e) => {
    setOrderBy(e.target.value);
  }, [setOrderBy]);

  useEffect(() => {
    const nextSearchParams = getSearchParamsFromSelectedTags(selectedTagsByGroup);

    setSearchParams(nextSearchParams, {
      // without this the back button gets hijacked
      // whenever the AllProducts view is opened
      replace: true
    });
  }, [setSearchParams, selectedTagsByGroup]);

  return (
    <div>
      <div className={classes.header}>
        <Typography variant='h1'>
          {headerText}
        </Typography>
        <div className={classes.sortAndFilter}>
          <OrderBy
            value={orderBy}
            onChange={handleOrderByChange}
            availableTagsByGroupName={availableTagsByGroupName}
          />
          {filtersInDrawer && (
            <div className={classes.filterByButton}>
              <FilterButton
                selectedTagsByGroup={selectedTagsByGroup}
                onClick={() => setFiltersOpen(true)}
              />
              <BBDrawer
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                heading='filter'
              >
                <FilterPicker
                  onContentLanguageChange={onContentLanguageChange}
                  selectedTagsByGroup={selectedTagsByGroup}
                  onTagsChange={handleSelectedTagsChange}
                  showContentLanguagePicker={showContentLanguagePicker}
                  contentLanguage={contentLanguage}
                  availableTagsByGroupName={availableTagsByGroupName}
                  enabledTagsByGroupName={enabledTagsByGroupName}
                />
                {/*
                  doesn't quite do what it says on the tin
                  because the filters will have already updated by the time it's clicked
                  but provides a useful CTA for mobile users
                */}
                <div className={classes.updateButton}>
                  <StoreButton
                    label='update'
                    onClick={() => setFiltersOpen(false)}
                  />
                </div>
              </BBDrawer>
            </div>
          )}
        </div>
      </div>
      <Grid container>
        {!filtersInDrawer && (
          <Grid item md={3}>
            <Card className={classes.filterContainer}>
              <CardContent>
                <FilterPicker
                  onContentLanguageChange={onContentLanguageChange}
                  selectedTagsByGroup={selectedTagsByGroup}
                  onTagsChange={handleSelectedTagsChange}
                  showContentLanguagePicker={showContentLanguagePicker}
                  contentLanguage={contentLanguage}
                  availableTagsByGroupName={availableTagsByGroupName}
                  enabledTagsByGroupName={enabledTagsByGroupName}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid item xs={12} md={9}>
          <DynamoProductSearch
            options={{
              orderBy,
              // order in ascending order unless nothing ("id") is picked
              // in which case this shows latest products first
              orderByDirection: orderBy === 'id' ? 'desc' : 'asc',
              tags: hardcodedTags
                ? Object.values(selectedTagsByGroup).concat(hardcodedTags)
                : Object.values(selectedTagsByGroup),
              pagination: {
                pageSize: 10
              },
              language: convertLangCodeToGraphqlEnum(contentLanguage),
              currency: locale.currency
            }}
          >
            {({ products, loadMore, loading }) => (
              <>
                {
                  loading
                    ? (
                      <SearchResultsSkeleton />
                      )
                    : products && products.length > 0
                      ? (
                        <ListView
                          products={products}
                          hideAuthor={hideAuthor}
                        />
                        )
                      : (
                        <Typography className={classes.noProducts}>
                          <BBTranslate textId='no-matching-products-found' />.
                        </Typography>
                        )
                }
                {
                  loadMore && (
                    <LoadMoreScrollAnchor loadMore={loadMore} key={products && products.length} />
                  )
                }
              </>
            )}
          </DynamoProductSearch>
        </Grid>
      </Grid>
    </div>
  );
};

ProductsWithFilterPicker.propTypes = {
  headerText: PropTypes.node.isRequired,
  availableTagsByGroupName: PropTypes.object.isRequired,
  enabledTagsByGroupName: PropTypes.object,
  showContentLanguagePicker: PropTypes.bool,
  contentLanguage: PropTypes.string,
  onContentLanguageChange: PropTypes.func,
  hideAuthor: PropTypes.bool
};

export default ProductsWithFilterPicker;
